import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  BookOpen,
  Heart,
  Eye,
  Calendar,
  ArrowRight,
  FileVideo,
  Clock,
} from "lucide-react";
import { API_BASE_URL } from '../../config';
import { cn } from '../../lib/utils';
import { getPlatformIcon } from '../../utils/platformIcons';

interface DevlogPost {
  id: string;
  createdAt: string;
  textEn: string;
  media: {
    id: string;
    filePath: string;
    thumbnailPath: string | null;
    mediaType: string;
    fileSize: number | null;
    aiMetadata: {
      videoTitle?: string;
      description?: string;
      tags?: string[];
    } | null;
  } | null;
  stats: {
    totalLikes: number;
    totalViews: number;
    platforms: string[];
  };
}

interface DevlogContextResponse {
  daysSinceLastDevlog: number | null;
  lastDevlogDate: string | null;
  recentGameplayPosts: DevlogPost[];
}

interface DevlogMissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  onSelectMedia: (media: any) => void;
  onStartFromScratch: () => void;
}

export const DevlogMissionModal: React.FC<DevlogMissionModalProps> = ({
  isOpen,
  onClose,
  gameId,
  onSelectMedia,
  onStartFromScratch,
}) => {
  const [state, setState] = useState<'loading' | 'results' | 'empty'>('loading');
  const [context, setContext] = useState<DevlogContextResponse | null>(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!isOpen) return;

    setState('loading');
    setContext(null);

    const fetchContext = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/recommendations/devlog-context`, {
          params: { gameId },
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data as DevlogContextResponse;
        setContext(data);

        if (data.recentGameplayPosts.length > 0) {
          setState('results');
        } else {
          setState('empty');
        }
      } catch (error) {
        console.error('[DevlogMissionModal] Error fetching context:', error);
        setState('empty');
      }
    };

    fetchContext();
  }, [isOpen, gameId]);

  // Auto-redirect when no posts found
  useEffect(() => {
    if (state === 'empty') {
      const timer = setTimeout(() => {
        onStartFromScratch();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [state]);

  const getThumbnailUrl = (filePath: string, w: number = 300) => {
    return `${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(filePath)}&w=${w}&token=${token}`;
  };

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDaysAgoText = (days: number | null) => {
    if (days === null) return null;
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days} days ago`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-background border-border overflow-hidden">
        {/* Loading State */}
        {state === 'loading' && (
          <div className="py-12 text-center space-y-4">
            <div className="relative mx-auto w-16 h-16">
              <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20">
                <BookOpen className="h-7 w-7 text-primary animate-pulse" />
              </div>
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">Preparing your devlog context...</h3>
              <p className="text-xs text-muted-foreground mt-1">Looking at your recent gameplay posts</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {state === 'empty' && (
          <div className="py-12 text-center space-y-4">
            <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-muted/50 border border-border">
              <FileVideo className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">No recent gameplay posts</h3>
              <p className="text-xs text-muted-foreground mt-1">Opening create post...</p>
            </div>
          </div>
        )}

        {/* Results State */}
        {state === 'results' && context && (
          <>
            <DialogHeader>
              <DialogTitle className="text-sm font-black uppercase tracking-[0.15em] flex items-center gap-2">
                <div className="p-1.5 bg-primary/20 rounded-lg">
                  <BookOpen className="h-4 w-4 text-primary" />
                </div>
                Technical Devlog
              </DialogTitle>
            </DialogHeader>

            {/* Days since last devlog */}
            <div className={cn(
              "flex items-center gap-3 p-3 rounded-xl border",
              context.daysSinceLastDevlog === null
                ? "bg-amber-500/5 border-amber-500/20"
                : context.daysSinceLastDevlog > 7
                  ? "bg-orange-500/5 border-orange-500/20"
                  : "bg-green-500/5 border-green-500/20"
            )}>
              <Clock className={cn(
                "h-5 w-5 shrink-0",
                context.daysSinceLastDevlog === null ? "text-amber-400"
                  : context.daysSinceLastDevlog > 7 ? "text-orange-400"
                  : "text-green-400"
              )} />
              <div className="min-w-0">
                {context.daysSinceLastDevlog === null ? (
                  <p className="text-sm font-medium text-foreground">
                    You haven't posted a devlog yet - great time to start!
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-foreground">
                      Last devlog: <span className="font-bold">{getDaysAgoText(context.daysSinceLastDevlog)}</span>
                    </p>
                    {context.lastDevlogDate && (
                      <p className="text-[11px] text-muted-foreground">
                        {formatDate(context.lastDevlogDate)}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Recent gameplay posts */}
            <div className="space-y-1.5">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider px-1">
                Recent gameplay you could talk about
              </p>

              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {context.recentGameplayPosts.map((post) => {
                  const media = post.media;
                  const title = media?.aiMetadata?.videoTitle || 'Untitled';
                  const thumbPath = media?.thumbnailPath || media?.filePath;
                  const textSnippet = post.textEn
                    ? post.textEn.length > 60 ? post.textEn.substring(0, 60) + '...' : post.textEn
                    : null;

                  return (
                    <button
                      key={post.id}
                      onClick={() => media && onSelectMedia(media)}
                      disabled={!media}
                      className={cn(
                        "w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all duration-200",
                        "bg-background/50 border-border/50 hover:border-primary/30 hover:bg-muted/60",
                        "text-left group/item cursor-pointer",
                        !media && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {/* Thumbnail */}
                      <div className="relative shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted border border-border/50">
                        {thumbPath && (
                          <img
                            src={getThumbnailUrl(thumbPath)}
                            alt={title}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-foreground truncate group-hover/item:text-primary transition-colors">
                            {title}
                          </h4>
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground shrink-0">
                            <Calendar className="h-2.5 w-2.5" />
                            {formatDate(post.createdAt)}
                          </span>
                        </div>

                        {textSnippet && (
                          <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                            {textSnippet}
                          </p>
                        )}

                        {/* Stats */}
                        {(post.stats.totalLikes > 0 || post.stats.totalViews > 0) && (
                          <div className="flex items-center gap-2 mt-1">
                            {post.stats.totalLikes > 0 && (
                              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                <Heart className="h-2.5 w-2.5 text-red-400" />
                                {formatNumber(post.stats.totalLikes)}
                              </span>
                            )}
                            {post.stats.totalViews > 0 && (
                              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                <Eye className="h-2.5 w-2.5 text-primary" />
                                {formatNumber(post.stats.totalViews)}
                              </span>
                            )}
                            {post.stats.platforms.map(p => (
                              <span key={p} className="shrink-0">
                                {getPlatformIcon(p, 'h-2.5 w-2.5')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover/item:text-primary shrink-0 transition-colors" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Divider + Start from scratch */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-[10px] text-muted-foreground uppercase tracking-widest">or</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full text-xs text-muted-foreground hover:text-foreground border-border/50"
              onClick={onStartFromScratch}
            >
              Start from scratch
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
